// Opacity groups of a converted skeleton: rive-spine's companion runtime (`runtime/riveGroupAlpha.ts`, copied
// 2026-10-11, unchanged but for this header; the engine's copy since decision 72).
//
// Rive multiplies a node's opacity into everything under it. A converted skeleton bakes that into slot alphas wherever
// one animation owns all the factors; where two independently timed animations (two state machine layers, two Spine
// tracks) each fade part of the chain, the converter keys the group node's opacity on an attachment-less control slot
// named `alpha@<bone>`, and this multiplies it into every slot under that bone after each AnimationState.apply (the pot
// smoke's plume, the fire streak's body). A skeleton without control slots is left alone.
import type { AnimationState, Bone, Skeleton, Slot } from "@esotericsoftware/spine-pixi-v8";

export const GROUP_SLOT_PREFIX = "alpha@";

interface Group {
  control: Slot;
  members: Slot[];
}

function isUnder(bone: Bone | null, ancestor: Bone): boolean {
  for (let b = bone; b; b = b.parent) if (b === ancestor) return true;
  return false;
}

/** Hooks the groups into `state.apply`; returns a function that removes the hook. */
export function installGroupAlpha(state: AnimationState, skeleton: Skeleton): () => void {
  const groups: Group[] = [];
  for (const control of skeleton.slots) {
    if (!control.data.name.startsWith(GROUP_SLOT_PREFIX)) continue;
    const members = skeleton.slots.filter((s) => !s.data.name.startsWith(GROUP_SLOT_PREFIX) && isUnder(s.bone, control.bone));
    groups.push({ control, members });
  }
  if (groups.length === 0) return () => {};

  // Each member's own alpha (what its timelines or setup pose gave it) and the alpha this wrote over it: the own value
  // comes back before the next apply, unless something else has changed the slot since.
  const own = new Map<Slot, { own: number; written: number }>();
  const apply = state.apply.bind(state);
  state.apply = (target: Skeleton): boolean => {
    for (const [slot, a] of own) if (slot.color.a === a.written) slot.color.a = a.own;
    own.clear();
    const applied = apply(target);
    if (target !== skeleton) return applied;
    for (const g of groups) for (const m of g.members) if (!own.has(m)) own.set(m, { own: m.color.a, written: m.color.a });
    for (const g of groups) {
      const factor = g.control.bone.active ? g.control.color.a : 1;
      if (factor === 1) continue;
      for (const m of g.members) m.color.a *= factor;
    }
    for (const [slot, a] of own) a.written = slot.color.a;
    return applied;
  };
  return () => {
    state.apply = apply;
    for (const [slot, a] of own) if (slot.color.a === a.written) slot.color.a = a.own;
  };
}
